/**
 * AuthContext.js
 * Manages authentication state and operations for Constellation KG
 * Works with backend's token-based auth using HTTP-only cookies
 */

import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { authService } from '../services/api';
import AcceptanceGate, { hasAcceptedCurrentDocuments } from '../components/AcceptanceGate/AcceptanceGate';
import wsService from '../services/wsService';

// =========================================
// Context Configuration
// =========================================

export const AuthContext = createContext(null);

const AUTH_ERROR_MESSAGES = {
  NETWORK: 'Unable to connect to server',
  SERVER_ERROR: 'Authentication service error',
  SESSION_EXPIRED: 'Your session has expired. Please login again'
};

// How long logout() waits for POST /api/auth/logout before giving up. The
// response carries the cookie-clearing Set-Cookie headers, so the wait is
// required for correctness; the bound is only there so an unreachable backend
// cannot leave a caller waiting indefinitely.
const LOGOUT_REQUEST_TIMEOUT_MS = 5000;

/**
 * AuthProvider Component
 * Provides authentication state and operations to app
 */
export const AuthProvider = ({ children }) => {
  // =========================================
  // State Management
  // =========================================
  
  const [state, setState] = useState({
    user: null,
    sidebarCounts: null,
    error: null,
    isLoading: true,
    isInitialized: false
  });

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // =========================================
  // Logout Prevention Guards
  // =========================================
  
  // This ref prevents multiple simultaneous logout attempts
  const isLoggingOut = useRef(false);
  
  // These refs prevent infinite refresh loops
  const refreshAttemptRef = useRef(0);
  const lastRefreshAttempt = useRef(Date.now());
  const MAX_REFRESH_ATTEMPTS = 3;
  const REFRESH_TIMEOUT = 5000; // 5 seconds

  // =========================================
  // Error Handling
  // =========================================

  const handleAuthError = useCallback((error, context) => {
    console.error(`Auth error in ${context}:`, {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });

    let errorMessage = error.response?.data?.message || AUTH_ERROR_MESSAGES.SERVER_ERROR;

    if (!error.response) {
      errorMessage = AUTH_ERROR_MESSAGES.NETWORK;
    } else if (error.response.status === 401 && context !== 'login') {
      errorMessage = AUTH_ERROR_MESSAGES.SESSION_EXPIRED;
    }

    updateState({
      error: errorMessage,
      isLoading: false,
      // Only clear user on session expiry or other auth failures
      ...(context !== 'login' && { user: null })
    });
  }, [updateState]);

  // =========================================
  // Token Refresh with Retry Prevention
  // =========================================

  const refreshAuthToken = useCallback(async () => {
    // Don't try to refresh if we're logging out
    if (isLoggingOut.current) {
      console.log('Skipping refresh - logout in progress');
      return false;
    }

    const now = Date.now();
    const timeSinceLastAttempt = now - lastRefreshAttempt.current;
    
    // If we've attempted refresh too many times recently, give up
    if (refreshAttemptRef.current >= MAX_REFRESH_ATTEMPTS && timeSinceLastAttempt < REFRESH_TIMEOUT) {
      console.error('Too many refresh attempts - stopping to prevent loop');
      return false;
    }
    
    // Reset counter if it's been a while since last attempt
    if (timeSinceLastAttempt > REFRESH_TIMEOUT) {
      refreshAttemptRef.current = 0;
    }
    
    lastRefreshAttempt.current = now;
    refreshAttemptRef.current += 1;

    console.log(`Token refresh attempt ${refreshAttemptRef.current}/${MAX_REFRESH_ATTEMPTS}`);

    try {
      const response = await authService.refresh();
      updateState({
        user: response.user,
        sidebarCounts: response.sidebarCounts ?? null,
        error: null
      });
      refreshAttemptRef.current = 0; // Reset on success
      console.log('Token refresh successful');
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      
      // Don't cascade into logout - just return false
      // Let the calling code decide what to do
      return false;
    }
  }, [updateState]);

  // =========================================
  // Auth Operations
  // =========================================

  /**
   * Initialize authentication state
   * Checks if user has valid session
   */
  const initializeAuth = useCallback(async () => {
    // Don't initialize if we're in the middle of logging out
    if (isLoggingOut.current) {
      return;
    }

    try {
      const response = await authService.checkStatus();
      
      // Double-check we didn't start logging out while waiting for response
      if (isLoggingOut.current) {
        return;
      }

      updateState({
        user: response.user,
        sidebarCounts: response.sidebarCounts ?? null,
        isLoading: false,
        isInitialized: true,
        error: null
      });
    } catch (error) {
      // If we're logging out, ignore all errors
      if (isLoggingOut.current) {
        return;
      }

      if (error.response?.status === 401) {
        // Not authenticated is a valid state - don't try to refresh during init
        // Refreshing during init can cause loops if the session truly expired.
        //
        // Race guard: this status check is fired on mount, which on the
        // /verify-magic-link route runs concurrently with the magic-link
        // verification. That check is sent before the session cookie exists,
        // so it resolves 401 — but by the time it lands, setUserFromMagicLink
        // may already have authenticated the user. Do NOT clobber an
        // authenticated user with this stale 401 (doing so churns the
        // WebSocket effect through connect()/disconnect() and leaves the
        // socket closed). Only resolve the init flags in that case.
        setState(prev => prev.user
          ? { ...prev, isLoading: false, isInitialized: true, error: null }
          : {
              ...prev,
              user: null,
              sidebarCounts: null,
              isLoading: false,
              isInitialized: true,
              error: null
            });
      } else {
        handleAuthError(error, 'initialization');
      }
    }
  }, [updateState, handleAuthError]);

  /**
   * Handle user login
   * Authenticates user and updates state with user data
   */
  const login = useCallback(async (email, password) => {
    // Reset logout flag when logging in
    isLoggingOut.current = false;
    refreshAttemptRef.current = 0;
    
    updateState({ isLoading: true, error: null });
    
    try {
      const response = await authService.login({ email, password });
      updateState({
        user: response.user,
        sidebarCounts: response.sidebarCounts ?? null,
        isLoading: false,
        error: null
      });
      return true;
    } catch (error) {
      handleAuthError(error, 'login');
      return false;
    }
  }, [updateState, handleAuthError]);

  /**
   * Handle user logout
   * SIMPLIFIED APPROACH: Prioritize immediate state clearing over API success
   * This prevents logout loops by ensuring components stop making requests immediately
   */
  const logout = useCallback(async () => {
    // Prevent multiple simultaneous logout attempts
    if (isLoggingOut.current) {
      console.log('Logout already in progress');
      return;
    }

    console.log('Starting logout process');
    isLoggingOut.current = true;
    
    // Reset refresh counters to prevent any refresh attempts
    refreshAttemptRef.current = MAX_REFRESH_ATTEMPTS;
    lastRefreshAttempt.current = Date.now();

    // STEP 1: Clear local state IMMEDIATELY
    // This makes isAuthenticated = false, which stops all components from making requests
    updateState({
      user: null,
      sidebarCounts: null,
      error: null,
      isLoading: false
    });

    // STEP 2: Clear all local storage immediately
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }

    // STEP 3: Call backend logout and WAIT for it to come back.
    //
    // This must be awaited, not fired and forgotten. The `token` and
    // `refreshToken` cookies are httpOnly (COOKIE_OPTIONS in
    // ckg3-backend/routes/auth.js), so the storage clearing in STEP 2 cannot
    // touch them — the ONLY thing that deletes them is the Set-Cookie headers
    // on this response. A caller that navigates away (window.location) before
    // the response is processed tears down the document with those headers
    // unapplied, and the cookie survives; the next GET /api/auth/check then
    // re-authenticates from it and the user is silently logged back in.
    //
    // Awaiting here does NOT delay isAuthenticated going false — STEP 1 already
    // cleared local state synchronously — so the logout-loop guard that
    // motivated the original fire-and-forget is fully preserved.
    //
    // Bounded by a timeout so an unreachable or hanging backend cannot strand
    // the caller. On timeout we proceed anyway: local state is already gone, so
    // the worst case is the pre-existing behaviour rather than a hung UI.
    let serverLogoutConfirmed = false;
    let timeoutId;
    try {
      await Promise.race([
        authService.logout(),
        new Promise((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('Logout request timed out')),
            LOGOUT_REQUEST_TIMEOUT_MS
          );
        })
      ]);
      serverLogoutConfirmed = true;
    } catch (error) {
      console.error('Backend logout did not complete (continuing anyway):', error);
    } finally {
      clearTimeout(timeoutId);
    }

    // Returned so callers that navigate on completion can tell a confirmed
    // server-side logout from a timed-out one. Existing callers ignore it.
    return serverLogoutConfirmed;
  }, [updateState]);
  
  /**
   * Set user from magic link verification
   * Updates state after successful magic link login
   * (Magic link verification already handles authentication on backend)
   *
   * Writes the full canonical auth state — including sidebarCounts and
   * isInitialized — so this path is symmetric with login()/initializeAuth().
   * Marking isInitialized here also makes this the authoritative resolution
   * of auth state, so a concurrent mount-time initializeAuth() can't leave
   * the app in a half-initialized state.
   */
  const setUserFromMagicLink = useCallback((user, sidebarCounts = null) => {
    // Reset logout flag
    isLoggingOut.current = false;
    refreshAttemptRef.current = 0;

    updateState({
      user: user,
      sidebarCounts: sidebarCounts ?? null,
      isLoading: false,
      isInitialized: true,
      error: null
    });

    console.log('User logged in via magic link');
  }, [updateState]);

  /**
   * Update user profile
   * Updates user data and refreshes state
   */
  const updateUser = useCallback(async (userData) => {
    updateState({ isLoading: true, error: null });

    try {
      const response = await authService.updateProfile(userData);
      updateState({
        user: response.user,
        isLoading: false,
        error: null
      });
      return true;
    } catch (error) {
      handleAuthError(error, 'update-profile');
      return false;
    }
  }, [updateState, handleAuthError]);

  // =========================================
  // Effects
  // =========================================

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Tie the WebSocket lifecycle to auth state. connect/disconnect are
  // idempotent in wsService, so transient re-renders don't churn the
  // socket. The pipe carries signals only — no content — and never
  // opens before the user is authenticated.
  useEffect(() => {
    if (state.user) {
      wsService.connect();
    } else {
      wsService.disconnect();
    }
  }, [state.user?._id]);

  // =========================================
  // Context Value
  // =========================================

  const contextValue = {
    user: state.user,
    sidebarCounts: state.sidebarCounts,
    error: state.error,
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,
    isAuthenticated: !!state.user,
    isAdmin: state.user?.isAdmin || false,
    // Tier information
    userTier: state.user?.tier || 'freemium',
    userRole: state.user?.role || 'user',
    hasSubscriberAccess: ['subscriber', 'champion', 'alpha', 'enterprise'].includes(state.user?.tier) || state.user?.role === 'admin',
    login,
    logout,
    updateUser,
    setUserFromMagicLink
  };

  // The acceptance gate renders HERE — inside the provider, above the route
  // table — so both paths that produce an authenticated state (login() and
  // setUserFromMagicLink()) pass through it by construction. No route wrapper,
  // no per-route ternary edits. Admins are gated like everyone else.
  //
  // It is a sibling of {children} rather than a wrapper so the app behind it
  // stays mounted; the overlay covers it and traps focus.
  const showAcceptanceGate =
    !!state.user && !hasAcceptedCurrentDocuments(state.user);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <AcceptanceGate
        isOpen={showAcceptanceGate}
        user={state.user}
        onAccepted={(updatedUser) => updateState({ user: updatedUser })}
        onDecline={async () => {
          // logout() clears state but does not navigate; the route ternaries in
          // App.js would land an unauthenticated user on /login. The gate's
          // decline path is specified to return them to the landing page, so
          // redirect explicitly. Full page load (matching MyAccount.js's
          // post-password-change redirect) also drops any in-memory state.
          //
          // The await is load-bearing: logout() now waits for the server's
          // cookie-clearing response, and navigating before it lands is exactly
          // what left the session alive. Do not turn this back into a bare call.
          await logout();

          // replace() rather than href so the gated route is not left in
          // history — Back from the landing page must not return the user to a
          // page they just declined their way out of.
          window.location.replace('/');
        }}
      />
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
